import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AssetDetailSection from './AssetDetailSection';
import InputField from '../../../components/ui/InputField';
import { OpeningHourEntry, Weekday } from '../../../types/locations';

interface OpeningHoursSectionProps {
    openingHours: OpeningHourEntry[];
    onChange?: (hours: OpeningHourEntry[]) => void;
    onValidityChange?: (isValid: boolean) => void;
    /**
     * 'edit' (default) renders editable From/To inputs per weekday and
     * surfaces validity to the parent. 'view' renders a read-only summary
     * with "From - To" or "Closed" per day and ignores onChange.
     */
    mode?: 'view' | 'edit';
    /**
     * What NO opening hours at all mean for this kind of object — the two
     * readers disagree by design. Venues (asset pages): bookable around the
     * clock, as the end-user venue search treats them
     * (`backend/src/core/lib/openingHoursFit.ts`) and as `computeBookableHours`
     * counts them (#1959). Stations: closed every day — the availability
     * service has no slot on a weekday without a row (see
     * `new-backend/admin/src/lib/location/locationWrite.ts`). Defaults to the
     * station reading so a new caller never promises "always open" by
     * accident.
     */
    emptyMeansAlwaysOpen?: boolean;
    /** A line under the heading — e.g. who the hours apply to (house page). */
    hint?: string;
}

const WEEKDAYS: Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

interface DayDraft {
    from: string;
    to: string;
}
type DraftMap = Record<Weekday, DayDraft>;

type RowError = 'incomplete' | 'range' | 'order' | null;

const emptyDraft = (): DraftMap =>
    WEEKDAYS.reduce((acc, day) => {
        acc[day] = { from: '', to: '' };
        return acc;
    }, {} as DraftMap);

const draftFromEntries = (entries: OpeningHourEntry[]): DraftMap => {
    const draft = emptyDraft();
    for (const entry of entries) {
        if (WEEKDAYS.includes(entry.weekday as Weekday)) {
            draft[entry.weekday as Weekday] = {
                from: String(entry.times.fromHour),
                to: String(entry.times.toHour),
            };
        }
    }
    return draft;
};

// Validate a single row. Both empty = closed that day (no error). Both
// filled and well-formed = open. Anything else is flagged so the user
// can't silently submit half-typed hours.
const validateRow = ({ from, to }: DayDraft): RowError => {
    if (from === '' && to === '') return null;
    if (from === '' || to === '') return 'incomplete';
    const fromHour = Number(from);
    const toHour = Number(to);
    if (!Number.isInteger(fromHour) || !Number.isInteger(toHour)) return 'range';
    if (fromHour < 0 || fromHour > 24 || toHour < 0 || toHour > 24) return 'range';
    if (fromHour >= toHour) return 'order';
    return null;
};

// Only valid rows are surfaced to the parent; partial / invalid rows
// stay in local UI state until the user fixes them.
const draftToEntries = (draft: DraftMap): OpeningHourEntry[] => {
    const entries: OpeningHourEntry[] = [];
    for (const day of WEEKDAYS) {
        if (validateRow(draft[day]) !== null) continue;
        const { from, to } = draft[day];
        if (from === '' && to === '') continue;
        entries.push({
            weekday: day,
            times: { fromHour: Number(from), toHour: Number(to) },
        });
    }
    return entries;
};

const OpeningHoursSection: React.FC<OpeningHoursSectionProps> = ({
    openingHours,
    onChange,
    onValidityChange,
    mode = 'edit',
    emptyMeansAlwaysOpen = false,
    hint,
}) => {
    const { t } = useTranslation();

    // Local draft so the user can clear one side of a row without us
    // dropping the other side from the parent's array mid-edit. View
    // mode bypasses the draft entirely (see below), so the initial
    // mount-time value is the only one we need.
    const [draft, setDraft] = useState<DraftMap>(() => draftFromEntries(openingHours));

    const dayLabels = useMemo<Record<Weekday, string>>(() => ({
        mon: t('asset_detail.weekdays.mon'),
        tue: t('asset_detail.weekdays.tue'),
        wed: t('asset_detail.weekdays.wed'),
        thu: t('asset_detail.weekdays.thu'),
        fri: t('asset_detail.weekdays.fri'),
        sat: t('asset_detail.weekdays.sat'),
        sun: t('asset_detail.weekdays.sun'),
    }), [t]);

    const rowErrors = useMemo(() => {
        const errors = {} as Record<Weekday, RowError>;
        for (const day of WEEKDAYS) {
            errors[day] = validateRow(draft[day]);
        }
        return errors;
    }, [draft]);

    const isValid = useMemo(
        () => WEEKDAYS.every((day) => rowErrors[day] === null),
        [rowErrors],
    );

    // #1959: the backend reads an empty config and a partial config in opposite
    // directions — no rows at all means bookable 24/7 (`computeBookableHours`
    // returns 24 when the list is empty), while any row present closes every
    // weekday that has none. So the first day a tenant fills in silently closes
    // the other six, and the static hint above ("leave both empty for days when
    // the venue is closed") reads as if that were true of an empty form too.
    // Counting entries rather than filled rows: this is exactly what gets sent.
    const configuredDays = useMemo(() => draftToEntries(draft).length, [draft]);

    // Notify the parent only when validity actually flips, to keep its
    // setState calls out of every keystroke.
    const lastValidityRef = useRef<boolean | null>(null);
    useEffect(() => {
        if (lastValidityRef.current === isValid) return;
        lastValidityRef.current = isValid;
        onValidityChange?.(isValid);
    }, [isValid, onValidityChange]);

    const errorMessageFor = (err: RowError): string | null => {
        switch (err) {
            case 'incomplete': return t('asset_detail.fields.opening_hours_error_incomplete');
            case 'range': return t('asset_detail.fields.opening_hours_error_range');
            case 'order': return t('asset_detail.fields.opening_hours_error_order');
            default: return null;
        }
    };

    const handleChange = (day: Weekday, field: keyof DayDraft, val: string) => {
        // Allow only digits; let empty through so the user can clear the field.
        if (val !== '' && !/^\d+$/.test(val)) return;
        const next: DraftMap = { ...draft, [day]: { ...draft[day], [field]: val } };
        setDraft(next);
        onChange?.(draftToEntries(next));
    };

    // View mode: read directly from the prop so changes to the parent
    // array (e.g. when the asset details load after the initial render)
    // are reflected without any local draft state.
    if (mode === 'view') {
        const byDay = new Map<Weekday, OpeningHourEntry>();
        for (const entry of openingHours) {
            if (WEEKDAYS.includes(entry.weekday as Weekday)) {
                byDay.set(entry.weekday as Weekday, entry);
            }
        }
        // A venue with no hours at all is open around the clock, not closed
        // on seven days — saying "Geschlossen" here contradicted the edit
        // form's own hint and the end-user search.
        const alwaysOpen = emptyMeansAlwaysOpen && byDay.size === 0;
        const emptyDayLabel = alwaysOpen
            ? t('asset_detail.fields.opening_hours_always')
            : t('asset_detail.fields.opening_hours_closed');
        return (
            <AssetDetailSection title={t('asset_detail.sections.opening_hours')} subtitle={hint}>
                <div className={'flex flex-col gap-2'}>
                    {WEEKDAYS.map((day) => {
                        const entry = byDay.get(day);
                        return (
                            <div
                                key={day}
                                className={'grid grid-cols-[120px_minmax(0,1fr)] gap-3 items-center'}
                            >
                                <span className={'text-sm font-medium text-foreground'}>
                                    {dayLabels[day]}
                                </span>
                                <span className={'text-sm text-foreground/80'}>
                                    {entry
                                        ? `${entry.times.fromHour} – ${entry.times.toHour} ${t('asset_detail.fields.opening_hour_suffix')}`
                                        : emptyDayLabel}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </AssetDetailSection>
        );
    }

    return (
        <AssetDetailSection title={t('asset_detail.sections.opening_hours')} subtitle={hint}>
            <div className={'flex flex-col gap-3'}>
                {WEEKDAYS.map((day) => {
                    const error = rowErrors[day];
                    const message = errorMessageFor(error);
                    return (
                        <div key={day} className={'flex flex-col gap-1'}>
                            <div className={'grid grid-cols-[120px_minmax(0,1fr)_minmax(0,1fr)] gap-3 items-center'}>
                                <span className={'text-sm font-medium text-foreground'}>
                                    {dayLabels[day]}
                                </span>
                                <InputField
                                    type={'number'}
                                    label={t('asset_detail.fields.opening_from')}
                                    value={draft[day].from}
                                    onChange={(val) => handleChange(day, 'from', val)}
                                    min={0}
                                    max={24}
                                    integerOnly={true}
                                    suffix={t('asset_detail.fields.opening_hour_suffix')}
                                />
                                <InputField
                                    type={'number'}
                                    label={t('asset_detail.fields.opening_to')}
                                    value={draft[day].to}
                                    onChange={(val) => handleChange(day, 'to', val)}
                                    min={0}
                                    max={24}
                                    integerOnly={true}
                                    suffix={t('asset_detail.fields.opening_hour_suffix')}
                                />
                            </div>
                            {message && (
                                <p className={'text-xs text-red-600 pl-[132px]'}>{message}</p>
                            )}
                        </div>
                    );
                })}
                {/* One hint at a time, matching what the current entries mean:
                    on an empty venue form "leave empty for closed days" would
                    contradict "no hours = around the clock" right below it. */}
                {(configuredDays === WEEKDAYS.length || (configuredDays === 0 && !emptyMeansAlwaysOpen)) && (
                    <p className={'text-xs text-foreground/60'}>
                        {t('asset_detail.fields.opening_hours_hint')}
                    </p>
                )}
                {configuredDays === 0 && emptyMeansAlwaysOpen && (
                    <p className={'text-xs text-foreground/60'}>
                        {t('asset_detail.fields.opening_hours_hint_none')}
                    </p>
                )}
                {configuredDays > 0 && configuredDays < WEEKDAYS.length && (
                    <p className={'text-xs text-foreground/60'}>
                        {t('asset_detail.fields.opening_hours_hint_partial')}
                    </p>
                )}
            </div>
        </AssetDetailSection>
    );
};

export default OpeningHoursSection;
